// The wizard's failure surfaces (Module 02 §5, §9.1), as one shared set so every
// screen reports the same conflict the same way.
//
// `WizardProblemNotice` is the one screens usually want: hand it any thrown
// error and it picks the right panel — stale payload (with a "load the current
// version" action), preconditions unmet, already live, resource busy — falling
// back to the shared §4 error card (title + detail + request id + retry).

import { useTranslation } from "react-i18next";
import type { components } from "@assessly/api-client";
import { Button } from "../../../components/button";
import { ErrorState } from "../../../components/states";
import { formatTimestamp } from "../../../lib/datetime";
import { requestIdOf } from "../../../lib/request";
import {
  isAlreadyLive,
  isResourceBusy,
  preconditionFailure,
  preconditionLabelKey,
  stalePayloadServerStep,
} from "../lib/wizard-problems";

type S = components["schemas"];

/**
 * 409 STALE_PAYLOAD (§9.1). The server's current step state travels in the
 * problem; the only safe recovery is to show it and let the user re-apply their
 * edit, never a silent overwrite.
 */
export function StaleConflictNotice({
  serverStep,
  onReload,
}: {
  serverStep: S["OnboardingStepState"];
  onReload: () => void;
}) {
  const { t, i18n } = useTranslation();
  return (
    <div className="state state-error wizard-notice" role="alert" data-testid="stale-conflict">
      <p className="state-title">{t("onboarding.conflict.staleTitle")}</p>
      <p className="state-detail">{t("onboarding.conflict.staleBody")}</p>
      <p className="state-detail">
        {t("onboarding.conflict.staleSavedAt", {
          time: formatTimestamp(serverStep.updated_at, i18n.language),
        })}
      </p>
      <Button onClick={onReload}>{t("onboarding.conflict.staleReload")}</Button>
    </div>
  );
}

/** 422 PRECONDITIONS_UNMET — the listed keys, in the tenant's language. */
export function PreconditionsNotice({
  unmet,
  stepNo,
}: {
  unmet: readonly string[];
  stepNo?: number | undefined;
}) {
  const { t } = useTranslation();
  return (
    <div className="state state-error wizard-notice" role="alert" data-testid="preconditions-unmet">
      <p className="state-title">{t("onboarding.preconditions.title")}</p>
      {stepNo !== undefined ? (
        <p className="state-detail">{t("onboarding.preconditions.stepNote", { number: stepNo })}</p>
      ) : null}
      <p className="state-detail">{t("onboarding.preconditions.body")}</p>
      <ul className="wizard-notice-list">
        {unmet.map((key) => (
          <li key={key}>{t(preconditionLabelKey(key), { defaultValue: key })}</li>
        ))}
      </ul>
    </div>
  );
}

/** 409 ALREADY_LIVE — the wizard is frozen; nothing here is editable again. */
export function AlreadyLiveNotice() {
  const { t } = useTranslation();
  return (
    <div className="state state-empty wizard-notice" role="status" data-testid="already-live">
      <p className="state-title">{t("onboarding.conflict.alreadyLiveTitle")}</p>
      <p className="state-body">{t("onboarding.conflict.alreadyLiveBody")}</p>
    </div>
  );
}

/** 503 RESOURCE_BUSY — the per-tenant wizard advisory lock is held. */
export function BusyNotice({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="state state-error wizard-notice" role="alert" data-testid="resource-busy">
      <p className="state-title">{t("onboarding.conflict.busyTitle")}</p>
      <p className="state-detail">{t("onboarding.conflict.busyBody")}</p>
      <Button onClick={onRetry}>{t("onboarding.conflict.busyRetry")}</Button>
    </div>
  );
}

export interface WizardProblemNoticeProps {
  /** The thrown error; `null`/`undefined` renders nothing. */
  error: unknown;
  /** Retry the failed action (also used by the busy panel). */
  onRetry: () => void;
  /**
   * Called with the server's current step state when the failure was a
   * STALE_PAYLOAD conflict — the screen re-seeds its editor from it. Without
   * this handler the stale panel still explains the conflict, but its "load the
   * current version" button falls back to `onRetry`.
   */
  onReloadServerStep?: ((step: S["OnboardingStepState"]) => void) | undefined;
  /** Title for the generic fallback card. */
  titleKey?: string;
  detailKey?: string;
}

export function WizardProblemNotice({
  error,
  onRetry,
  onReloadServerStep,
  titleKey = "onboarding.state.errorTitle",
  detailKey = "onboarding.state.errorDetail",
}: WizardProblemNoticeProps) {
  const { t } = useTranslation();
  if (error === null || error === undefined) return null;

  const stale = stalePayloadServerStep(error);
  if (stale !== undefined) {
    return (
      <StaleConflictNotice
        serverStep={stale}
        onReload={() => {
          if (onReloadServerStep !== undefined) onReloadServerStep(stale);
          else onRetry();
        }}
      />
    );
  }

  const preconditions = preconditionFailure(error);
  if (preconditions !== undefined) {
    return <PreconditionsNotice unmet={preconditions.unmet} stepNo={preconditions.stepNo} />;
  }

  if (isAlreadyLive(error)) return <AlreadyLiveNotice />;
  if (isResourceBusy(error)) return <BusyNotice onRetry={onRetry} />;

  return (
    <ErrorState
      title={t(titleKey)}
      detail={t(detailKey)}
      retryLabel={t("onboarding.state.retry")}
      onRetry={onRetry}
      requestId={requestIdOf(error)}
      requestIdLabel={t("onboarding.state.requestId")}
    />
  );
}
